<template>
  <div class="emp-root">

    <!-- ═══════════════════════════════════════════
         HEADER
    ═══════════════════════════════════════════ -->
    <div class="emp-header">
      <div>
        <h1 class="emp-title">Employees</h1>
        <p class="emp-subtitle">{{ employees.length }} personnel records</p>
      </div>
      <div class="emp-header-controls">
        <div class="search-wrap">
          <span class="search-icon">🔍</span>
          <input
            class="search-input"
            placeholder="Search..."
            v-model="search"
            @input="page = 0"
          />
        </div>

        <filter-button-group
          :options="statusOptions"
          :value="statusFilter"
          @change="val => { statusFilter = val; page = 0; }"
        />

        <filter-button-group
          :options="siteOptions"
          :value="siteFilter"
          @change="val => { siteFilter = val; page = 0; }"
          type="site"
        />

        <button class="btn btn-primary" @click="openNew">
          <span class="btn-plus">+</span> New employee
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════
         BODY
    ═══════════════════════════════════════════ -->
    <div class="emp-body">
      <div class="emp-main">

        <div class="stat-grid">
          <stat-card label="All employees"  :value="employees.length"  icon="👥" color="#5B6BFF" />
          <stat-card label="Active"         :value="activeCount"       icon="✅" color="#1A9E5C" />
          <stat-card label="Inactive"       :value="inactiveCount"     icon="⏸️" color="#E05252" />
          <stat-card label="Position types" :value="positionTypeCount" icon="🏷️" color="#F59E0B" />
        </div>

        <div class="table-wrap">
          <table class="emp-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Position</th>
                <th>Department</th>
                <th>Site</th>
                <th>Status</th>
                <th>Start of employment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(emp, i) in paginated"
                :key="emp.id"
                class="emp-row"
                :class="{
                  'emp-row--selected': detailEmp && detailEmp.id === emp.id,
                  'emp-row--alt': i % 2 !== 0
                }"
                @click="toggleDetail(emp)"
              >
                <td class="td-employee">
                  <employee-avatar :emp="emp" />
                  <div>
                    <div class="emp-name">{{ getFullName(emp) }}</div>
                    <div class="emp-email">{{ emp.user ? emp.user.email : '' }}</div>
                  </div>
                </td>
                <td class="td-text">{{ emp.position && emp.position.translations && emp.position.translations.hu ? emp.position.translations.hu.name : '—' }}</td>
                <td><dep-badge :name="emp.department && emp.department.translations && emp.department.translations.hu ? emp.department.translations.hu.name : null" /></td>
                <td class="td-text">{{ emp.site ? emp.site.name : '—' }}</td>
                <td><status-badge :status="emp.status" /></td>
                <td class="td-text">{{ emp.work_start || '—' }}</td>
                <td>
                  <div class="row-actions">
                    <button class="btn btn-edit"   @click.stop="openEdit(emp)">✏️ Edit</button>
                    <button class="btn btn-delete" @click.stop="deleteConfirm = emp">🗑️</button>
                  </div>
                </td>
              </tr>
              <tr v-if="paginated.length === 0">
                <td colspan="7" class="td-empty">No results found for your search criteria.</td>
              </tr>
            </tbody>
          </table>

          <div class="pagination">
            <span class="pagination-info">
              Showing
              {{ filtered.length === 0 ? 0 : page * rowsPerPage + 1 }}–{{ Math.min((page + 1) * rowsPerPage, filtered.length) }}
              of {{ filtered.length }}
            </span>
            <div class="pagination-btns">
              <button
                v-for="p in totalPages"
                :key="p"
                class="btn btn-page"
                :class="{ 'btn-page--active': page === p - 1 }"
                @click="page = p - 1"
              >{{ p }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Detail panel backdrop -->
      <div v-if="detailEmp" class="detail-backdrop" @click="detailEmp = null" />

      <!-- Detail panel -->
      <transition name="slide-right">
        <detail-panel
          v-if="detailEmp"
          :emp="detailEmp"
          @close="detailEmp = null"
          @edit="openEdit"
          @delete="emp => deleteConfirm = emp"
        />
      </transition>
    </div>

    <!-- ═══════════════════════════════════════════
         MODAL: New / Edit
    ═══════════════════════════════════════════ -->
    <transition name="fade">
      <div v-if="modalOpen" class="modal-overlay" @click.self="modalOpen = false">
        <div class="modal-box">
          <employee-form
            :form-data="formData"
            :is-edit="!!editEmpId"
            :saving="saving"
            :permissions="permissions"
            :positions="positionCollection || []"
            :departments="departmentCollection || []"
            :sites="siteCollection || []"
            :educations="educationCollection || []"
            @save="handleSave"
            @cancel="modalOpen = false"
            @update-field="updateField"
            @files-changed="pendingFiles = $event"
          />
        </div>
      </div>
    </transition>

    <!-- ═══════════════════════════════════════════
         DELETE CONFIRM
    ═══════════════════════════════════════════ -->
    <transition name="fade">
      <div v-if="deleteConfirm" class="modal-overlay">
        <div class="confirm-box">
          <div class="confirm-icon">⚠️</div>
          <h3 class="confirm-title">Confirm deletion</h3>
          <p class="confirm-text">
            Are you sure you want to delete
            <strong>{{ getFullName(deleteConfirm) }}</strong>?
            This action cannot be undone.
          </p>
          <div class="confirm-actions">
            <button class="btn btn-secondary" @click="deleteConfirm = null" :disabled="deleting">Cancel</button>
            <button class="btn btn-danger"    @click="handleDelete" :disabled="deleting">
              {{ deleting ? '⏳ Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>


<script>
import StatCard          from './components/StatCard.vue';
import FilterButtonGroup from './components/FilterButtonGroup.vue';
import EmployeeAvatar    from './components/EmployeeAvatar.vue';
//import SiteBadge         from './components/SiteBadge.vue';
import DepBadge         from './components/DepBadge.vue';
import StatusBadge       from './components/StatusBadge.vue';
import DetailPanel       from './components/DetailPanel.vue';
import EmployeeForm      from './components/EmployeeForm.vue';
import { mapGetters, mapActions } from 'vuex';
import ApiService from '@/core/services/api.service';

const ENDPOINT = 'humanResources/employee';

/* ─────────────────────────────────────────────
   HELPERS
   Real API shape:
     emp.surname          → family name
     emp.christian_name1  → first name
     emp.christian_name2  → middle name
     emp.fullName         → pre-built "surname christian_name1" string from backend
     emp.status           → 1 (active) | 0 (inactive)  — integer!
     emp.user.email       → email
     emp.user.surname     → mirrors emp.surname
     emp.user.christian_name1 → mirrors emp.christian_name1
───────────────────────────────────────────── */

function getFullName(emp) {
  if (!emp) return '';
  // Backend already provides a fullName string — use it when available
  if (emp.fullName) return emp.fullName;
  const surname = emp.surname || '';
  const first   = emp.christian_name1 || '';
  return `${surname} ${first}`.trim();
}

function emptyEmployee() {
  return {
    id:                   null,
    // top-level name fields
    surname:              '',
    christian_name1:      '',
    christian_name2:      '',
    fullName:             '',
    // user object (for display / email)
    user: {
      email:           '',
      surname:         '',
      christian_name1: '',
      christian_name2: '',
      locale:          'hu',
      login:           1,
      portal:          0,
      newsletter:      0,
      status:          1,
    },
    // top-level API fields
    email:                '',
    locale:               'hu',
    login:                1,
    portal:               0,
    newsletter:           0,
    status:               1,
    is_active:            1,
    // relations — store both id and object for display
    position:             null,
    position_id:          null,
    department:           null,
    department_id:        null,
    site:                 null,
    site_id:              null,
    education:            null,
    education_id:         null,
    // personal
    birth_place:          null,
    birth_date:           null,
    birth_name:           null,
    mother_name:          null,
    // documents
    vat_number:           null,
    identity_card_number: null,
    address_card_number:  null,
    insurance_number:     null,
    // employment
    work_start:           null,
    work_end:             null,
    // arrays
    addresses:            [{ address: null }],
    contact_data:         [{ phone_number: null }],
    employee_documents:  [],
    comment:              null,
    // Admin / salary fields
    fuel_allowance:       null,
    hourly_rate:          null,
    vacation_rate:        null,
    base_salary:          null,
    attachments:          null,
    medical_end:          null,
    fire_safety_end:          null,
  };
}

/*function setNestedVal(obj, path, value) {
  const keys  = path.split('.');
  const clone = JSON.parse(JSON.stringify(obj));
  let cursor  = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!cursor[keys[i]]) cursor[keys[i]] = {};
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return clone;
}*/

/**
 * Builds the exact payload the backend expects.
 * Flattens nested objects into _id fields,
 * pulls top-level fields from user object where needed.
 */
function buildPayload(f) {
  return {
    email:                f.email                || (f.user && f.user.email)  || '',
    surname:              f.surname              || '',
    christian_name1:      f.christian_name1      || '',
    christian_name2:      f.christian_name2      || null,
    locale:               f.locale               || (f.user && f.user.locale) || 'hu',
    login:                f.login                ?? 0,
    portal:               f.portal               ?? 0,
    newsletter:           f.newsletter           ?? 0,
    status:               f.status               ?? 1,
    is_active:            f.is_active            ?? f.status ?? 1,
    contact_data:         f.contact_data         || [],
    birth_place:          f.birth_place          || null,
    birth_date:           f.birth_date           || null,
    birth_name:           f.birth_name           || null,
    mother_name:          f.mother_name          || null,
    vat_number:           f.vat_number           || null,
    identity_card_number: f.identity_card_number || null,
    address_card_number:  f.address_card_number  || null,
    insurance_number:     f.insurance_number     || null,
    addresses:            f.addresses            || [],
    position_id:          f.position_id          || (f.position  && f.position.id)  || null,
    education_id:         f.education_id         || (f.education && f.education.id) || null,
    work_start:           f.work_start           || null,
    work_end:             f.work_end             || null,
    employee_documents:     f.employee_documents || [],
    department_id:        f.department_id        || (f.department && f.department.id) || null,
    site_id:              f.site_id              || (f.site      && f.site.id)      || null,
    comment:              f.comment              || null,
    fuel_allowance:       f.fuel_allowance       ?? null,
    hourly_rate:          f.hourly_rate          ?? null,
    vacation_rate:        f.vacation_rate        ?? null,
    base_salary:          f.base_salary          ?? null,
    attachments:          f.attachments          || null,
    medical_end:          f.medical_end          || null,
    fire_safety_end:          f.fire_safety_end          || null,
  };
}

/* ─────────────────────────────────────────────
   PAGE COMPONENT
───────────────────────────────────────────── */
export default {
  name: 'Employees',

  components: {
    StatCard,
    FilterButtonGroup,
    EmployeeAvatar,
    //SiteBadge,
    DepBadge,
    StatusBadge,
    DetailPanel,
    EmployeeForm,
  },

  data() {
    return {
      employees:     [],
      search:        '',
      statusFilter:  'all',
      siteFilter:    'all',
      page:          0,
      rowsPerPage:   10,
      modalOpen:     false,
      editEmpId:     null,
      detailEmp:     null,
      formData:      emptyEmployee(),
      deleteConfirm: null,
      saving:        false,
      deleting:      false,
      pendingFiles:  [],
      permissions:   [],

      statusOptions: [
        { value: 'all', label: 'All'      },
        { value: 1,     label: 'Active'   },
        { value: 0,     label: 'Inactive' },
      ],
      siteOptions: [
        { value: 'all',       label: 'All'       },
        { value: 'Kismetál',  label: 'Kismetál'  },
        { value: 'Construct', label: 'Construct' },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'employeeCollection',
      'departmentCollection',
      'siteCollection',
      'positionCollection',
      'educationCollection',
      'statuses',
    ]),

    filtered() {
      return this.employees.filter(e => {
        const name        = getFullName(e).toLowerCase();
        const pos         = e.position && e.position.translations && e.position.translations.hu ? e.position.translations.hu.name.toLowerCase() : '';
        const dep         = e.department && e.department.translations && e.department.translations.hu ? e.department.translations.hu.name.toLowerCase() : '';
        const q           = this.search.toLowerCase();
        const matchSearch = name.includes(q) || pos.includes(q) || dep.includes(q);

        // status is an integer (1/0); 'all' means no filter
        const matchStatus = this.statusFilter === 'all' || e.status === this.statusFilter;
        const matchSite   = this.siteFilter   === 'all' || (e.department && e.department.translations && e.department.translations.hu && e.department.translations.hu.name === this.siteFilter);

        return matchSearch && matchStatus && matchSite;
      });
    },

    paginated() {
      const start = this.page * this.rowsPerPage;
      return this.filtered.slice(start, start + this.rowsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filtered.length / this.rowsPerPage);
    },

    activeCount() {
      return this.employees.filter(e => e.status === 1).length;
    },
    inactiveCount() {
      return this.employees.filter(e => e.status !== 1).length;
    },
    positionTypeCount() {
      return new Set(
        this.employees.map(e => e.position && e.position.name).filter(Boolean)
      ).size;
    },
  },

  watch: {
    employeeCollection(newVal) {
      this.employees = JSON.parse(JSON.stringify(newVal || []));
      if (this.detailEmp) {
        const fresh = this.employees.find(e => e.id === this.detailEmp.id);
        this.detailEmp = fresh ? JSON.parse(JSON.stringify(fresh)) : null;
      }
    },
  },

  methods: {
    ...mapActions([
      'fetchEmployee',
      'fetchDepartment',
      'fetchSite',
      'fetchEducation',
      'fetchPosition',
    ]),

    // Expose to template
    getFullName(emp) {
      return getFullName(emp);
    },

    toggleDetail(emp) {
      if (this.detailEmp && this.detailEmp.id === emp.id) {
        this.detailEmp = null;
      } else {
        this.detailEmp = JSON.parse(JSON.stringify(emp));
      }
    },

    openNew() {
      this.formData  = emptyEmployee();
      this.editEmpId = null;
      this.modalOpen = true;
    },

    openEdit(emp) {
      const fd = JSON.parse(JSON.stringify(emp));

      // Flatten user fields
      if (!fd.email      && fd.user) fd.email      = fd.user.email      || '';
      if (!fd.locale     && fd.user) fd.locale     = fd.user.locale     || 'hu';
      if (fd.login      == null && fd.user) fd.login      = fd.user.login      ?? 1;
      if (fd.portal     == null && fd.user) fd.portal     = fd.user.portal     ?? 0;
      if (fd.newsletter == null && fd.user) fd.newsletter = fd.user.newsletter ?? 0;

      // Flatten salary_setting into top-level fields so the form can bind directly
      if (fd.salary_setting) {
        fd.fuel_allowance = fd.salary_setting.fuel_allowance ?? null;
        fd.hourly_rate    = fd.salary_setting.hourly_rate    ?? null;
        fd.vacation_rate  = fd.salary_setting.vacation_rate  ?? null;
        fd.base_salary    = fd.salary_setting.base_salary    ?? null;
      }

      this.formData  = fd;
      this.editEmpId = emp.id;
      this.modalOpen = true;
    },

    updateField(key, value) {
      let clone = JSON.parse(JSON.stringify(this.formData));

      // Relation fields: value is { id, name } object — set both object and _id
      const RELATIONS = { position: 'position_id', department: 'department_id', site: 'site_id', education: 'education_id' };
      if (RELATIONS[key] && value && typeof value === 'object') {
        clone[key]            = value;
        clone[RELATIONS[key]] = value.id;
      } else {
        // Generic dot-notation path set
        const keys   = key.split('.');
        let cursor   = clone;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!cursor[keys[i]]) cursor[keys[i]] = {};
          cursor = cursor[keys[i]];
        }
        cursor[keys[keys.length - 1]] = value;
      }

      this.formData = clone;
    },

    async handleSave() {
      this.saving = true;
      try {
        const payload = buildPayload(this.formData);
        const fd = new FormData();
        
        let requestData;
        if (this.pendingFiles.length > 0) {
          Object.keys(payload).forEach((key) => {
            if (key === 'employee_documents') {
              return;
            }
          
            const value = payload[key];
          
            if(key === 'addresses'){
              fd.append(key, payload.addresses);
              return;
            }
            if(key === 'contact_data'){
              fd.append(key, payload.contact_data);
              return;
            }
          
            if (value === null || value === undefined) {
              fd.append(key, '');
              return;
            }
          
            if (Array.isArray(value)) {
              fd.append(key, JSON.stringify(value));
              return;
            }
          
            if (typeof value === 'object') {
              fd.append(key, JSON.stringify(value));
              return;
            }
          
            fd.append(key, value);
          });
          // Append new files
          this.pendingFiles.forEach((file, index) => {
            fd.append(`employee_documents[${index}]`, file);
          });
          fd.append('_method', 'PUT')
          requestData = fd;
        } else {
          requestData = payload;
        }


        if (this.editEmpId) {
          console.log(requestData);
          const { data } = await ApiService.post(`${ENDPOINT}/${this.editEmpId}`, requestData);
          const updated = Object.assign(
            {},
            JSON.parse(JSON.stringify(this.formData)),
            data.data || data
          );
          const parts = [updated.surname, updated.christian_name1, updated.christian_name2].filter(Boolean);
          updated.fullName = parts.join(' ');
          const idx = this.employees.findIndex(e => e.id === this.editEmpId);
          if (idx !== -1) this.$set(this.employees, idx, updated);
          if (this.detailEmp && this.detailEmp.id === this.editEmpId) {
            this.detailEmp = JSON.parse(JSON.stringify(updated));
          }
        } else {
          const { data } = await ApiService.post(ENDPOINT, requestData);
          const created = Object.assign(
            {},
            JSON.parse(JSON.stringify(this.formData)),
            data.data || data
          );
          const createdParts = [created.surname, created.christian_name1, created.christian_name2].filter(Boolean);
          created.fullName = createdParts.join(' ');
          this.employees.push(created);
        }

        this.pendingFiles = [];
        this.modalOpen    = false;
      } catch (e) {
        console.error('Save failed:', e);
      } finally {
        this.saving = false;
      }
    },

    async handleDelete() {
      const emp = this.deleteConfirm;
      this.deleting = true;
      try {
        await ApiService.delete(`${ENDPOINT}/${emp.id}`);
        this.employees = this.employees.filter(e => e.id !== emp.id);
        if (this.detailEmp && this.detailEmp.id === emp.id) this.detailEmp = null;
        this.deleteConfirm = null;
      } catch (e) {
        console.error('Delete failed:', e);
      } finally {
        this.deleting = false;
      }
    },

    async fetchModel() {
      await Promise.all([
        this.fetchDepartment(),
        this.fetchPosition(),
        this.fetchSite(),
        this.fetchEducation(),
      ]);
      await this.fetchEmployee();
      this.employees = JSON.parse(JSON.stringify(this.employeeCollection || []));
    },

    setPermissions() {
      if (localStorage.permissions) {
        this.permissions = JSON.parse(localStorage.permissions).filter(function(item) {
          return item.name.indexOf('humanResources.employee') > -1;
        });
      }
    },
  },

  async mounted() {
    this.setPermissions();
    await this.fetchModel();
  },
};
</script>


<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@600&display=swap');

.emp-root { font-family: 'DM Sans', 'Segoe UI', sans-serif; min-height: 100vh; background: #F0F2F7; color: #1a1a2e; }

/* ── Header ── */
.emp-header          { background: #fff; border-bottom: 1px solid #E8EBF5; padding: 16px 32px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.emp-title           { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 600; color: #1a1a2e; margin: 0; }
.emp-subtitle        { font-size: 13px; color: #9DA5C0; margin: 2px 0 0; }
.emp-header-controls { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

/* ── Search ── */
.search-wrap  { position: relative; }
.search-icon  { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9DA5C0; font-size: 14px; pointer-events: none; }
.search-input { background: #fff; border: 1.5px solid #E2E6F0; border-radius: 12px; padding: 10px 16px 10px 40px; font-size: 14px; font-family: inherit; outline: none; width: 260px; transition: border-color .15s, box-shadow .15s; }
.search-input:focus { border-color: #5B6BFF; box-shadow: 0 0 0 3px rgba(91,107,255,.12); }

/* ── Filter groups ── */
.filter-group { display: flex; background: #F0F2F7; border-radius: 12px; padding: 3px; gap: 2px; }
.filter-btn   { cursor: pointer; border: none; background: transparent; font-family: inherit; font-size: 13px; font-weight: 600; color: #7B82A0; border-radius: 9px; padding: 7px 13px; display: flex; align-items: center; gap: 5px; transition: background .15s, color .15s; white-space: nowrap; }
.filter-btn:hover:not(.filter-btn--active) { background: #e4e7f0; color: #4A5068; }
.filter-dot   { width: 7px; height: 7px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

/* ── Buttons ── */
.btn           { cursor: pointer; border: none; font-family: inherit; font-weight: 600; border-radius: 10px; transition: filter .15s, transform .15s; display: inline-flex; align-items: center; gap: 6px; }
.btn:hover     { filter: brightness(.92); transform: translateY(-1px); }
.btn:active    { transform: translateY(0); }
.btn-primary   { background: linear-gradient(135deg, #5B6BFF, #8B5BFF); color: #fff; padding: 10px 20px; font-size: 14px; }
.btn-secondary { background: #F0F4FA; color: #4A5068; padding: 8px 14px; font-size: 13px; }
.btn-edit      { background: #EEF1FF; color: #5B6BFF; padding: 6px 12px; font-size: 12px; }
.btn-delete    { background: #FEF0F0; color: #E05252; padding: 6px 10px; font-size: 12px; }
.btn-danger    { background: #E05252; color: #fff; padding: 11px 20px; font-size: 14px; }
.btn-sm        { padding: 6px 12px !important; font-size: 12px !important; }
.btn-plus      { font-size: 18px; line-height: 1; }

/* ── Body ── */
.emp-body { display: flex; height: calc(100vh - 73px); position: relative; }
.emp-main { flex: 1; overflow: auto; padding: 24px 32px; }

/* ── Stats ── */
.stat-grid  { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
.stat-card  { background: #fff; border-radius: 14px; padding: 16px 20px; border: 1px solid #E8EBF5; display: flex; justify-content: space-between; align-items: center; }
.stat-label { font-size: 11px; color: #9DA5C0; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; margin: 0; }
.stat-value { font-size: 28px; font-weight: 700; color: #1a1a2e; margin: 4px 0 0; }
.stat-icon  { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }

/* ── Table ── */
.table-wrap  { background: #fff; border-radius: 16px; border: 1px solid #E8EBF5; overflow: hidden; }
.emp-table   { width: 100%; border-collapse: collapse; }
.emp-table thead tr { background: #F8F9FD; }
.emp-table th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #9DA5C0; text-transform: uppercase; letter-spacing: .06em; border-bottom: 1px solid #E8EBF5; white-space: nowrap; }
.emp-row          { cursor: pointer; transition: background .15s, transform .15s; border-bottom: 1px solid #F0F4FA; background: #fff; }
.emp-row--alt     { background: #FAFBFF; }
.emp-row--selected{ background: #EEF1FF !important; }
.emp-row:hover    { background: #EEF1FF !important; transform: translateX(2px); }
.emp-row:hover .row-actions { opacity: 1; }
.row-actions  { opacity: 0; transition: opacity .15s; display: flex; gap: 6px; }
.td-employee  { padding: 12px 16px; display: flex; align-items: center; gap: 10px; }
.td-text      { padding: 12px 16px; font-size: 13px; color: #4A5068; }
.td-empty     { padding: 48px; text-align: center; color: #9DA5C0; font-size: 14px; }
.td-empty-val { color: #C5CAD8; }
.emp-name     { font-weight: 600; font-size: 14px; color: #1a1a2e; }
.emp-email    { font-size: 12px; color: #9DA5C0; }

/* ── Avatar ── */
.avatar { width: 38px; height: 38px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-weight: 700; color: #fff; font-size: 12px; flex-shrink: 0; }
.detail-avatar.avatar { width: 72px; height: 72px; border-radius: 20px; font-size: 24px; margin: 0 auto 12px; }

/* ── Badges ── */
.badge-status            { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.badge-status .badge-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; display: inline-block; }
.badge-status--active    { background: #E6F9F0; color: #1A9E5C; }
.badge-status--inactive  { background: #FEF0F0; color: #E05252; }
.badge-site              { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.badge-site .badge-dot   { width: 6px; height: 6px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

/* ── Pagination ── */
.pagination       { padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #F0F4FA; }
.pagination-info  { font-size: 13px; color: #9DA5C0; }
.pagination-btns  { display: flex; gap: 6px; }
.btn-page         { width: 32px; height: 32px; padding: 0; font-size: 13px; background: #F0F4FA; color: #4A5068; justify-content: center; }
.btn-page--active { background: #5B6BFF !important; color: #fff !important; }

/* ── Detail panel ── */
.detail-backdrop  { position: fixed; inset: 0; z-index: 49; }
.detail-panel     { position: fixed; right: 0; top: 0; bottom: 0; width: min(420px, 90vw); background: #fff; box-shadow: -10px 0 40px rgba(0,0,0,.10); z-index: 50; overflow-y: auto; display: flex; flex-direction: column; }
.detail-header    { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px 16px; }
.detail-hero      { text-align: center; padding: 0 24px 20px; border-bottom: 1px solid #F0F2F7; }
.detail-name      { font-size: 20px; font-weight: 700; color: #1a1a2e; margin: 0 0 4px; }
.detail-position  { font-size: 13px; color: #9DA5C0; margin: 0; }
.detail-body      { padding: 20px 24px; flex: 1; }
.detail-section   { margin-bottom: 20px; }
.detail-section-title { font-size: 10px; font-weight: 700; color: #7B82A0; text-transform: uppercase; letter-spacing: .1em; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #F0F2F7; }
.detail-row       { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; padding: 5px 0; }
.detail-key       { font-size: 12px; color: #9DA5C0; font-weight: 500; min-width: 130px; }
.detail-val       { font-size: 13px; color: #1a1a2e; font-weight: 600; text-align: right; }
.detail-comment   { font-size: 13px; color: #4A5068; line-height: 1.6; margin: 0; }

/* ── Slide-in ── */
.slide-right-enter-active { animation: slideInRight .3s cubic-bezier(.16,1,.3,1); }
.slide-right-leave-active { animation: slideInRight .2s cubic-bezier(.16,1,.3,1) reverse; }
@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

/* ── Modal ── */
.modal-overlay { position: fixed; inset: 0; background: rgba(15,15,35,.55); backdrop-filter: blur(6px); z-index: 100; display: flex; align-items: center; justify-content: center; }
.modal-box     { background: #fff; border-radius: 20px; width: min(900px, 95vw); max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 25px 60px rgba(0,0,0,.2); }
.modal-inner   { display: flex; flex-direction: column; max-height: 90vh; }
.modal-head    { padding: 20px 24px 16px; border-bottom: 1px solid #F0F4FA; display: flex; justify-content: space-between; align-items: center; }
.modal-title   { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; margin: 0; }
.modal-tabs    { display: flex; gap: 6px; padding: 12px 24px 0; border-bottom: 1px solid #F0F4FA; }
.modal-body    { flex: 1; overflow-y: auto; padding: 24px; }
.modal-foot    { padding: 16px 24px; border-top: 1px solid #F0F4FA; display: flex; justify-content: flex-end; gap: 10px; }

/* ── Tabs ── */
.tab-btn                             { padding: 8px 16px; border: none; background: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #7B82A0; border-radius: 8px 8px 0 0; transition: all .15s; display: flex; align-items: center; gap: 6px; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.tab-btn:hover:not(.tab-btn--active) { background: #EEF1FF; color: #5B6BFF; }
.tab-btn--active                     { background: #5B6BFF; color: #fff; border-bottom-color: #5B6BFF; }

/* ── Form ── */
.form-grid        { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.form-field       { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: 1 / -1; }
.form-label       { font-size: 11px; font-weight: 600; color: #7B82A0; text-transform: uppercase; letter-spacing: .06em; }
.form-input       { padding: 10px 14px; border: 1.5px solid #E2E6F0; border-radius: 10px; font-size: 14px; font-family: inherit; color: #1a1a2e; background: #FAFBFF; transition: border-color .15s, box-shadow .15s; outline: none; }
.form-input:focus { border-color: #5B6BFF; box-shadow: 0 0 0 3px rgba(91,107,255,.12); background: #fff; }
.status-select           { padding: 5px 10px; border: 1.5px solid #E2E6F0; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 600; outline: none; cursor: pointer; }
.status-select--active   { color: #1A9E5C; }
.status-select--inactive { color: #E05252; }

/* ── Confirm ── */
.confirm-box     { background: #fff; border-radius: 16px; padding: 28px; width: 360px; box-shadow: 0 20px 50px rgba(0,0,0,.15); text-align: center; }
.confirm-icon    { font-size: 40px; margin-bottom: 12px; }
.confirm-title   { font-size: 18px; font-weight: 700; color: #1a1a2e; margin: 0 0 8px; }
.confirm-text    { font-size: 14px; color: #9DA5C0; margin: 0 0 20px; line-height: 1.6; }
.confirm-actions { display: flex; gap: 10px; }
.confirm-actions .btn { flex: 1; justify-content: center; }

/* ── Fade ── */
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ── Scrollbar ── */
::-webkit-scrollbar       { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: #f0f2f7; }
::-webkit-scrollbar-thumb { background: #c5cad8; border-radius: 3px; }
</style>

<style>
@media (min-width: 960px) and (max-width: 1263px) {
  .container { max-width: 970px !important; }
}
@media (min-width: 1264px) and (max-width: 1903px) {
  .container { max-width: 1555px !important; }
}
@media (min-width: 1904px) {
  .container { max-width: 2245px !important; }
}
</style>